import { BISHOP, BLACK_BIT, COLOR_MASK, KING, KNIGHT, PAWN, QUEEN, ROOK, TYPE_MASK, WHITE_BIT, moveTo, movePromo } from '../board.ts';
import { ZOMBIE_PAWN } from '../pieces.ts';
import type { MoveContext } from '../rules.ts';
import type { Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';

// Day 9 (draft). Pieces level up. A piece that captures is promoted one step on the spot:
// pawn → knight → bishop → rook → queen. Queens and kings stay as they are. A pawn that
// captures onto the last rank promotes as usual instead.

/** What each piece type becomes after a capture (0: no change). */
export const LEVEL_UP: number[] = [];
LEVEL_UP[PAWN] = KNIGHT;
LEVEL_UP[ZOMBIE_PAWN] = KNIGHT;
LEVEL_UP[KNIGHT] = BISHOP;
LEVEL_UP[BISHOP] = ROOK;
LEVEL_UP[ROOK] = QUEEN;

/** Whether `c` has nothing but its king. */
function bareKing(board: Int8Array, c: 0 | 1): boolean {
  const bit = c === 0 ? WHITE_BIT : BLACK_BIT;
  for (let sq = 0; sq < 64; sq++) if (board[sq] & bit && (board[sq] & TYPE_MASK) !== KING) return false;
  return true;
}

export const levelup: Variant = {
  id: 'levelup',
  info: {
    name: 'Level Up Chess',
    but: 'pieces level up',
    tagline: 'Every capture evolves your piece. Three kills and a pawn is a rook.',
    summary: 'A piece that captures **levels up**: pawn → knight → bishop → rook → queen. Feed your pawns.',
    rules: [
      'When a piece captures, it levels up on the spot: pawn → knight → bishop → rook → queen.',
      'Queens and kings don’t level up.',
      'A pawn capturing onto the last rank promotes as usual instead.',
      'Otherwise normal chess: checkmate wins; stalemate, repetition and the 50-move rule draw.',
    ],
    accent: '#a98bff',
    emoji: '🆙',
    bots: ['Level 1', 'Grinder', 'Power Leveler', 'Max Level', 'Final Form'],
  },
  startFen: STANDARD_FEN,
  // Any capture can level a piece up (two same-coloured bishops: one takes the other and is a
  // rook), so the usual dead positions only hold once one side has nothing but its king.
  insufficientMaterial: (pos) => bareKing(pos.board, 0) || bareKing(pos.board, 1),

  afterMove(ctx: MoveContext) {
    if (!ctx.captured || movePromo(ctx.move)) return;
    const next = LEVEL_UP[ctx.piece & TYPE_MASK];
    if (!next) return;
    const to = moveTo(ctx.move);
    const piece = next | (ctx.piece & COLOR_MASK);
    ctx.next.board[to] = piece;
    ctx.events?.push({ t: 'add', sq: to, piece, cause: 'level-up' });
  },
};
